import axiosInstance from '@/lib/axios';
import {
  CreateTemplatePayload,
  ListTemplatesParams,
  ListTemplatesResponse,
  TemplateResponse,
  CreateTemplateResponse,
  UpdateTemplateResponse,
  DeleteTemplateResponse,
  SubmitTemplateResponse,
  SyncTemplateResponse,
  UploadMediaResponse,
  MetaTemplatesResponse,
  ImportTemplatesResponse,
  SyncAllTemplatesResponse,
  TemplateStatsResponse,
} from '../types/whatsappTemplates.types';

/**
 * WhatsApp Templates Service
 * Handles all API operations for WhatsApp template management
 */
export const whatsappTemplatesService = {
  /**
   * Get list of templates for a chatbot with pagination
   */
  listTemplates: async (
    chatBotId: string,
    params: ListTemplatesParams = {}
  ): Promise<ListTemplatesResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get<ListTemplatesResponse>(
        `/wp-crm/templates/list/${chatBotId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  listTriggerTemplates: async (
    chatBotId: string,
    params: ListTemplatesParams = {}
  ): Promise<ListTemplatesResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await axiosInstance.get<ListTemplatesResponse>(
        `/wp-crm/templates/trigger-list/${chatBotId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching trigger templates:', error);
      throw error;
    }
  },

  /**
   * Get template statistics (counts by status)
   */
  getTemplateStats: async (chatBotId: string): Promise<TemplateStatsResponse> => {
    try {
      const response = await axiosInstance.get<TemplateStatsResponse>(
        `/wp-crm/templates/stats/${chatBotId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching template stats:', error);
      throw error;
    }
  },

  /**
   * Get a single template by ID
   */
  getTemplate: async (templateId: string): Promise<TemplateResponse> => {
    try {
      const response = await axiosInstance.get<TemplateResponse>(
        `/wp-crm/templates/${templateId}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching template:', error);
      throw error;
    }
  },

  /**
   * Create a new template
   */
  createTemplate: async (
    chatBotId: string,
    data: CreateTemplatePayload,
    status: 'draft' | 'submit' = 'draft' // Default to 'draft' if not provided
  ): Promise<CreateTemplateResponse> => {
    try {
      const response = await axiosInstance.post<CreateTemplateResponse>(
        `/wp-crm/templates/${chatBotId}?status=${status}`,
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('Error creating template:', error);
      throw error;
    }
  },

  /**
   * Update an existing template
   */
  updateTemplate: async (
    templateId: string,
    data: any,
    status?: 'draft' | 'submit'
  ): Promise<UpdateTemplateResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (status) queryParams.append('status', status);

      const response = await axiosInstance.put<UpdateTemplateResponse>(
        `/wp-crm/templates/${templateId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('Error updating template:', error);
      throw error;
    }
  },

  /**
   * Delete a template
   */
  deleteTemplate: async (templateId: string): Promise<DeleteTemplateResponse> => {
    try {
      const response = await axiosInstance.delete<DeleteTemplateResponse>(
        `/wp-crm/templates/${templateId}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  /**
   * Submit template to Meta for approval
   */
  submitTemplate: async (templateId: string): Promise<SubmitTemplateResponse> => {
    try {
      const response = await axiosInstance.post<SubmitTemplateResponse>(
        `/wp-crm/templates/${templateId}/submit`,
        {},
        {
          timeout: 60000, // 60 seconds for Meta API calls (increased from default 10s)
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error submitting template to Meta:', error);

      // Handle specific timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error(
          'Template submission to Meta is taking longer than expected. This is normal for the first submission. Please try again in a few moments.'
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      throw error;
    }
  },

  /**
   * Sync template status from Meta
   */
  syncTemplateStatus: async (templateId: string): Promise<SyncTemplateResponse> => {
    try {
      const response = await axiosInstance.get<SyncTemplateResponse>(
        `/wp-crm/templates/${templateId}/sync`,
        {
          timeout: 30000, // 30 seconds for sync operations
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error syncing template status:', error);

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error(
          'Syncing template status from Meta is taking longer than expected. Please try again.'
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      throw error;
    }
  },

  /**
   * Delete template from Meta and local database
   */
  deleteTemplateFromMeta: async (templateId: string): Promise<DeleteTemplateResponse> => {
    try {
      const response = await axiosInstance.delete<DeleteTemplateResponse>(
        `/wp-crm/templates/${templateId}/meta`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error deleting template from Meta:', error);
      throw error;
    }
  },

  /**
   * Upload media file directly to Meta and get media ID
   * @param chatBotId - The chatbot ID
   * @param file - The file to upload (image, video, or document)
   * @param type - Media type: 'image', 'video', or 'document'
   * @returns Media upload response with mediaId
   */
  uploadTemplateMedia: async (
    chatBotId: string,
    file: File | null,
    type: 'image' | 'video' | 'document',
    mediaUrl?: string
  ): Promise<UploadMediaResponse> => {
    try {
      if (mediaUrl) {
        const response = await axiosInstance.post<UploadMediaResponse>(
          `/wp-crm/templates/media/${chatBotId}`,
          { type, mediaUrl }
        );
        return response.data;
      }

      const formData = new FormData();
      if (file) formData.append('file', file);
      formData.append('type', type);

      const response = await axiosInstance.post<UploadMediaResponse>(
        `/wp-crm/templates/media/${chatBotId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000, // 2 minutes for large file uploads
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error uploading template media:', error);
      throw error;
    }
  },

  /**
   * Get all templates from Meta for a chatbot
   */
  getMetaTemplates: async (chatBotId: string): Promise<MetaTemplatesResponse> => {
    try {
      const response = await axiosInstance.get<MetaTemplatesResponse>(
        `/wp-crm/templates/meta/${chatBotId}`,
        {
          timeout: 30000, // 30 seconds for Meta API calls
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching Meta templates:', error);

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error(
          'Fetching templates from Meta is taking longer than expected. Please try again.'
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      throw error;
    }
  },

  /**
   * Import templates from Meta to database
   */
  importTemplatesFromMeta: async (chatBotId: string): Promise<ImportTemplatesResponse> => {
    try {
      const response = await axiosInstance.post<ImportTemplatesResponse>(
        `/wp-crm/templates/import-from-meta/${chatBotId}`,
        {},
        {
          timeout: 90000, // 90 seconds for import operations
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error importing templates from Meta:', error);

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error(
          'Import operation is taking longer than expected. This is normal when importing multiple templates. Please try again.'
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      throw error;
    }
  },

  /**
   * Sync all submitted templates status from Meta
   */
  syncAllTemplates: async (chatBotId: string): Promise<SyncAllTemplatesResponse> => {
    try {
      const response = await axiosInstance.post<SyncAllTemplatesResponse>(
        `/wp-crm/templates/sync-all/${chatBotId}`,
        {},
        {
          timeout: 90000, // 90 seconds for bulk sync operations
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error syncing all templates:', error);

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error(
          'Bulk sync operation is taking longer than expected. This is normal when syncing multiple templates. Please try again or sync templates individually.'
        );
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      throw error;
    }
  },
};

/**
 * Transform TemplateFormValues to CreateTemplatePayload
 * Helper function to convert form data to API payload
 */
export const transformFormToPayload = (
  formValues: any // TemplateFormValues from TemplateForm.tsx
): CreateTemplatePayload => {
  // Set template type based on header configuration
  let templateType: 'text' | 'image' | 'video' | 'document' = 'text';

  if (formValues.headerType && formValues.headerType !== 'none') {
    if (formValues.headerType === 'text') {
      templateType = 'text';
    } else {
      templateType = formValues.headerType as 'image' | 'video' | 'document';
    }
  }

  // Note: Backend service ensures only draft templates can be updated
  // and validation allows all fields for draft updates
  const payload: CreateTemplatePayload = {
    name: formValues.name,
    metaTemplateName: formValues.metaTemplateName,
    category: formValues.category,
    language: formValues.language,
    type: templateType,  // Now properly typed as required
    body: formValues.body,
    status:formValues.status,
    parameterFormat: formValues.parameterFormat || 'POSITIONAL', // Include parameter format
  };

  const templateLibraryIndustryIds = Array.isArray(formValues.templateLibraryIndustryIds)
    ? formValues.templateLibraryIndustryIds.filter(Boolean)
    : [];

  const templateLibrarySubcategoryIds = Array.isArray(formValues.templateLibrarySubcategoryIds)
    ? formValues.templateLibrarySubcategoryIds.filter(Boolean)
    : [];

  if (templateLibraryIndustryIds.length > 0) {
    payload.templateLibraryIndustryIds = templateLibraryIndustryIds;
  }

  if (templateLibrarySubcategoryIds.length > 0) {
    payload.templateLibrarySubcategoryIds = templateLibrarySubcategoryIds;
  }


  // Add header based on type
  if (formValues.headerType === 'text' && formValues.headerText) {
    // Text header - now supported by backend
    payload.header = {
      type: 'text',
      text: formValues.headerText,
    };
  } else if (
    formValues.headerType !== 'none' &&
    formValues.headerType !== 'text' &&
    formValues.header?.mediaHandle
  ) {
    // Media header (image, video, document)
    // mediaHandle is the Meta media ID obtained from uploadTemplateMedia
    payload.header = {
      type: formValues.headerType as 'image' | 'video' | 'document',
      mediaHandle: formValues.header.mediaHandle, // Use mediaHandle instead of mediaUrl
      filename: formValues.header.filename,
    };
  }

  // Add footer if provided
  if (formValues.footer?.trim()) {
    payload.footer = formValues.footer;
  }

  // Add buttons if any
  if (formValues.buttons && formValues.buttons.length > 0) {
    payload.buttons = formValues.buttons.filter(
      (button: any) => button.text?.trim()
    );
  }

  // Add variables if any
  if (formValues.variables && formValues.variables.length > 0) {
    payload.variables = formValues.variables.filter(
      (variable: any) => variable.fallback?.trim()
    );
  }

  return payload;
};

/**
 * Error handler for API responses
 * Extracts meaningful error messages from API responses
 */
export const handleApiError = (error: any): string => {
  // Handle timeout errors specifically
  if (error?.name === 'TimeoutError') {
    return error.message;
  }

  if (error?.code === 'ECONNABORTED' && error?.message?.includes('timeout')) {
    return 'Request timed out. The operation is taking longer than expected. Please try again.';
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.status === 401) {
    return 'Authentication required. Please log in again.';
  }

  if (error?.response?.status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (error?.response?.status === 404) {
    return 'Template not found. It may have been deleted.';
  }

  if (error?.response?.status >= 500) {
    return 'Server error. Please try again later.';
  }

  if (error?.message === 'Network Error') {
    return 'Network error. Please check your internet connection.';
  }

  return error?.message || 'An unexpected error occurred. Please try again.';
};
